import React, { useState } from 'react';
import { useWholesaleOrders, useDownloadOrders } from './wholesaleuser.hooks';
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Paper, TableContainer, Typography, Pagination,
  CircularProgress, Box, TextField, Stack, Divider,
  Button, Select, MenuItem, FormControl, InputLabel, Tooltip
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../store/slices/alertSlice';
import OrderDetails from './OrderDetailsPopup';
import { constants } from '../../constants';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DownloadIcon from '@mui/icons-material/Download';

import { convertGMTToLocal } from '../../common';

export default function ManageWholesaleOrders() {

  const dispatch = useDispatch();

  const [filters, setFilters] = useState({ from_date: '', to_date: '', customername: '' });
  const [tempFilters, setTempFilters] = useState(filters);

  const [page, setPage] = useState(1);
  const perPage = constants.PERPAGE;

  const [openPopup, setOpenPopup] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const popupOpen = (id) => {
    setOpenPopup(true);
    setSelectedId(id);
  };

  const popupClose = () => {
    setOpenPopup(false);
    setSelectedId(null);
  };
  const handleInputChange = (e) => {
    setTempFilters({ ...tempFilters, [e.target.name]: e.target.value });
  };


  const { data: orderData, isLoading, isError, error: useOrderError } = useWholesaleOrders({
    filters,
    page,
    perPage,
    action: constants.ORDER_ACTION.VIEW
  });

  const { mutate: downloadOrders, isPending: isDownloading } = useDownloadOrders();

  const handleDownload = () => {
    downloadOrders(
      { filters, action: constants.ORDER_ACTION.DOWNLOAD },
      {
        onSuccess: (response) => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const nameString = Object.values(filters)
  .filter(value => value !== '') 
  .join('_');
          link.setAttribute('download', 'orders_' + nameString + '.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        onError: (error) => {
          dispatch(
            showAlert({
              severity: 'error',
              title: error.message,
            })
          );
        }
      }
    );
  };
  

  if (isLoading  || isDownloading) {
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }




  if(isError)
    {
      dispatch(
        showAlert({
          severity: 'error',
          title: useOrderError?.response?.data?.message || 'Error fetching orders.',
        })
      );
    }

  return (
    <Paper sx={{ p: 3, mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Manage Wholesale Orders
      </Typography>

      {/* Filters */}
      <Box sx={{ my: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            name="from_date"
            type="date"
            label="From Date"
            slotProps={{ inputLabel: { shrink: true } }}
            value={tempFilters.from_date}
            onChange={handleInputChange}
            inputProps={{ placeholder: 'dd/mm/yyyy' }}
          />

            <TextField
            fullWidth
            name="to_date"
            type="date"
            label="To Date"
            slotProps={{ inputLabel: { shrink: true } }}
            value={tempFilters.to_date}
            onChange={handleInputChange}
            inputProps={{ placeholder: 'dd/mm/yyyy' }}
          />



          <TextField
            fullWidth
            name="customername"
            label="Customer Name"
            value={tempFilters.customername}
            onChange={handleInputChange}
 placeholder='Customer Name'
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Tooltip title="Filter">
          <Button
          variant="contained"
          onClick={() => {

            setFilters({ ...tempFilters });
            setPage(1);
          }}
              sx={{ height: '56px' }}
        >
         <FilterAltIcon />
        </Button>
        </Tooltip>


        <Tooltip title="Download Order Details">
        <Button
  variant="contained"
  color="secondary"
  onClick={handleDownload}
  disabled={isDownloading}
>
          {isDownloading ? 'Downloading...' : <DownloadIcon />}
        </Button>
        </Tooltip>



        <Button
          variant="contained"
          onClick={() => {
              setTempFilters({ from_date: '', to_date: '', customername: '' });
              setFilters({ from_date: '', to_date: '', customername: '' });
            setPage(1);
          }}
          sx={{ height: '56px' }}
        >
          Reset
        </Button>
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Orders Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Date</TableCell>
   
              <TableCell>Price</TableCell>
              <TableCell>Tax</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Net Payable</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Mode</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {orderData?.wholesaleorders?.data?.length > 0 ? (
              orderData.wholesaleorders.data.map((order) => {
                const orderPrice = Number(order.orderprice || 0);
                const taxAmount = Number(order.taxamount || 0);
                const discountAmount = Number(order.discountamount || 0);
                const netPayable = (orderPrice + taxAmount - discountAmount).toFixed(2);

                return (
              <TableRow key={order.id}>
      <TableCell>{order.id}</TableCell>
      <TableCell>{convertGMTToLocal(order.created_at)}</TableCell>
                    <TableCell>€ {orderPrice.toFixed(2)}</TableCell>
                    <TableCell>€ {taxAmount.toFixed(2)}</TableCell>
                    <TableCell>{discountAmount ? `€ ${discountAmount.toFixed(2)}` : 'N/A'}</TableCell>
                    <TableCell>€ {netPayable}</TableCell>
      <TableCell>
        {order.customername} <br />
        {order.customerphonenumber}
      </TableCell>
      <TableCell>
                      {constants.PAYMENT_STATUS.PAID == order.paymentstatus ? 'Paid' : 'Pending'}
      </TableCell>
      <TableCell>
                      {constants.PAYMENT_MODE.CASH == order.paymentmode
                        ? 'Cash'
                        : constants.PAYMENT_MODE.BANKTRANSFER == order.paymentmode
                        ? 'Bank Transfer'
                        : constants.PAYMENT_MODE.CARD == order.paymentmode
                        ? 'Card'
                        : constants.PAYMENT_MODE.PAYPAL == order.paymentmode
                        ? 'Paypal'
                        : '-NA-'}
      </TableCell>

      <TableCell>
                    <Tooltip title="View Details">
        <Button
          size="small"
          color="primary"
          onClick={() => popupOpen(order.id)}
                     
        >
        <VisibilityRoundedIcon />
        </Button>


        </Tooltip>



                  
      </TableCell>
    </TableRow>
                );
              })
) : (
  <TableRow>
                <TableCell colSpan={10} align="center">
      No records found.
    </TableCell>
  </TableRow>
)}

          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {orderData?.orders?.data?.length > 0 && (
      <Box mt={3} display="flex" justifyContent="center">
        <Pagination
          count={orderData?.orders?.last_page || 1}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>
    )}

      {selectedId && (
        <OrderDetails orderid={selectedId} open={openPopup} onClose={popupClose} />
      )}
    </Paper>
  );
}
